import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({ email });
      await fetch('https://readdy.ai/api/form/d7d7vqvsch5bvrijfdgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#1A0A1E] text-white pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <img src="/ikiru-logo.jpg" alt="IKIRU Epilepsy Foundation Logo" className="w-10 h-10 rounded-full object-cover" />
              <div>
                <span className="font-bold text-lg tracking-tight leading-none block text-white">IKIRU</span>
                <span className="text-xs tracking-widest uppercase text-purple-400">Epilepsy Foundation</span>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Giving Life, Hope, and Dignity to Persons Living with Epilepsy across Africa. We exist to fundamentally change the narrative around epilepsy.
            </p>
            <div className="flex items-center gap-4">
              {['ri-facebook-fill', 'ri-twitter-x-fill', 'ri-instagram-line', 'ri-linkedin-fill'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#7B2D8B] transition-colors cursor-pointer"
                >
                  <i className={`${icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-purple-400 mb-6">
              <a href="#navigate">Navigate</a>
            </h4>
            <ul className="space-y-3">
              {[
                { label: 'About Us', path: '/about' },
                { label: 'Our Programs', path: '/programs' },
                { label: 'Get Involved', path: '/get-involved' },
                { label: 'Contact Us', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-purple-400 mb-6">Stay Connected</h4>
            <p className="text-gray-400 text-sm mb-5 leading-relaxed">
              Get updates on our impact, programs, and ways to help persons living with epilepsy.
            </p>
            {submitted ? (
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <i className="ri-checkbox-circle-line text-lg"></i>
                <span>Thank you for subscribing!</span>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubscribe}
                className="flex flex-col sm:flex-row gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 px-4 py-2.5 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#7B2D8B]"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 rounded-full bg-[#7B2D8B] text-white text-sm font-semibold hover:bg-[#6a2578] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {submitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
            )}
            <div className="mt-8 space-y-2">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <i className="ri-mail-line text-[#7B2D8B]"></i>
                <span>info@ikiruepilepsy.org</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <i className="ri-phone-line text-[#7B2D8B]"></i>
                <span>+234 809 955 4455</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <i className="ri-phone-line text-[#7B2D8B]"></i>
                <span>+234 706 090 8252</span>
              </div>
              <div className="flex items-start gap-2 text-gray-400 text-sm">
                <i className="ri-map-pin-line text-[#7B2D8B] mt-0.5"></i>
                <span>235 Court Road, Kano, Nigeria</span>
              </div>
            </div>
          </div>
        </div>

        {/* Purple ribbon strip — epilepsy awareness symbol */}
        <div className="w-full h-1 rounded-full bg-gradient-to-r from-[#4B0082] via-[#7B2D8B] to-[#C084FC] mb-8"></div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">&copy; 2025 IKIRU – The Epilepsy Foundation. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="#" rel="nofollow" className="text-gray-500 hover:text-white text-sm transition-colors cursor-pointer">Privacy Policy</a>
            <a href="#" rel="nofollow" className="text-gray-500 hover:text-white text-sm transition-colors cursor-pointer">Terms of Use</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
